if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {};

      // Get options from element data and combine with this.options
      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      // Listen to form submit event
      this.form = this.querySelector('form');
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

      // Listen to variant change events
      this.variants = this.querySelector('[data-variant-selector]');
      this.addEventListener('variant-change', this.onVariantChange.bind(this));
    }

    onVariantChange(evt) {
      this.currentVariant = evt.detail.variant;
      this.toggleAddButton();

      this.updateMedia();
      this.updateURL();
      this.renderProductInfo();
    }

    updateMedia() {
      if (!this.currentVariant || !this.currentVariant?.featured_media) return;
      const newMedia = document.querySelector(
        `[data-media-id="${this.options.sectionId}-${this.currentVariant.featured_media.id}"]`
      );
      if (!newMedia) return;
      const parent = newMedia.parentElement;
      parent.prepend(newMedia);
      window.setTimeout(() => {
        parent.scroll(0, 0);
      });
    }

    // If this is a PDP main product form, update the url with the new variant
    updateURL() {
      if (this.options.scope != 'main') return;

      // check if pathname contains the product handle
      const pathname = document.location.pathname + '/';
      if (pathname.indexOf(`/${this.options.handle}/`) === -1) return;

      // Update the url variant parameter or add it if it doesn't exist
      const url = new URL(document.location);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url);
    }

    // Get the product data from the main-product section
    renderProductInfo() {
      if (!this.options.sectionId) return;

      fetch(
        `/products/${this.options.handle}?variant=${this.currentVariant.id}&section=${this.options.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          // update price
          this.updateElement(html, `price-${this.options.sectionId}`);

          // update inventory_quantity
          this.updateElement(html, `inventory-${this.options.sectionId}`);
        });
    }

    // Replace any element based on the ID with the new html
    updateElement(html, selector) {
      const source = html.getElementById(selector);
      const destination = document.getElementById(selector);

      if (source && destination) {
        if (destination.tagName === 'INPUT') {
          destination.value = source.value;
        } else {
          destination.innerHTML = source.innerHTML;
        }
      }
    }

    /**
     * toggleAddButton
     * @param {Boolean} available
     * @returns {void}
     */
    toggleAddButton() {
      this.addButton = this.addButton || this.querySelector('[name="add"]');
      this.addButtonText =
        this.addButtonText || this.addButton.querySelector('[data-add-to-cart-button-text]');

      // Dis/enable the button
      if (!this.addButton) return;
      this.addButton.disabled = !this.currentVariant.available;

      // Toggle text
      if (!this.addButtonText) return;
      if (!this.currentVariant.available) {
        this.addButtonText.textContent = window.variantStrings.soldOut;
      } else {
        this.addButtonText.textContent = window.variantStrings.addToCart;
      }
    }

    onSubmitHandler(evt) {
      evt.preventDefault();

      this.submitButton = this.submitButton || this.querySelector('[type="submit"]');
      this.submitButton.setAttribute('disabled', true);
      this.submitButton.classList.add('button--loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      this.cartNotification = this.cartNotification || document.querySelector('cart-notification');
      this.cartDrawer = this.cartDrawer || document.querySelector('cart-drawer');

      this.cartItems = document.querySelector('cart-items');

      let sectionsToFetch = [
        ...this.cartNotification.getSectionsToRender().map((section) => section.section),
      ];

      if (this.cartItems) {
        sectionsToFetch = [
          ...sectionsToFetch,
          ...this.cartItems.getSectionsToRender().map((section) => section.section),
        ];
        // return unique values of array
        sectionsToFetch = [...new Set(sectionsToFetch)];
      }

      const formData = new FormData(this.form);
      formData.append('sections', sectionsToFetch);
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      // Get the current variant data from the variant component
      this.currentVariant = this.querySelector('[data-variant-selector]').getCurrentVariantData();

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((parsedState) => {
          // When a quantity error is returned
          if (parsedState.status == 422) {
            this.onCartError(parsedState, sectionsToFetch);
            return;
          } else {
            this.onCartSuccess(parsedState);
          }
          // Trigger cart updated event with the latest cart data and sections to render
          this.dispatchEvent(
            new CustomEvent('cart-updated', {
              bubbles: true,
              detail: {
                ...parsedState,
              },
            })
          );
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('button--loading');
          this.submitButton.removeAttribute('disabled');
        });
    }

    // When item is added to the cart
    onCartSuccess(parsedState) {
      if (this.cartDrawer) {
        // Render the returned sections
        this.cartDrawer.renderContents(parsedState);
      }

      // Show the notification or open the drawer
      switch (this.submitButton.dataset.addToCartBehavior) {
        case 'open_cart_drawer':
          if (this.cartDrawer) {
            // Set timeout to force animation. Because content is just updated with renderContents
            setTimeout(() => {
              this.cartDrawer.open();
            });
          }
          break;
        case 'show_cart_notification':
          if (this.cartNotification) {
            this.cartNotification.setActiveElement(document.activeElement);
            this.cartNotification.renderContents(parsedState);
          }
      }
    }

    // Handle any AddToCart errors
    onCartError(parsedState, sectionsToFetch) {
      if (this.cartNotification) {
        this.cartNotification.setActiveElement(document.activeElement);
        this.cartNotification.renderCartError(parsedState.description);
      }

      if (this.cartDrawer) {
        // Let the cart drawer update itself to show all available products are added to the cart
        fetch('/?sections=' + sectionsToFetch.join())
          .then((response) => response.json())
          .then((parsedState) => {
            this.cartDrawer.renderContents({ sections: parsedState });
          });
      }
    }
  }

  window.ProductForm = ProductForm;
  customElements.define('product-form', ProductForm);
}
